import { z } from 'zod';
import { cellRowTypeSchema } from './cell.js';
import { palierNumberSchema } from './palier.js';
import type { UserRole } from './user.js';

/**
 * Matrix and folder notes (issue #19, `docs/specs/matrices.md` « Notes de
 * matrice »): a memo per matrix, and signed, dated discussions on a target.
 * One level of replies; a reply inherits the visibility of its root.
 */

// ── Limits ───────────────────────────────────────────────────────────────────

/** A note or a reply: 1 to 2000 characters, plain text, line breaks kept. */
export const NOTE_BODY_MAX = 2000;
/** The matrix memo: 0 to 4000 characters. */
export const MEMO_MAX = 4000;
/** `MatrixSummary.memoPreview`: the first characters of the memo, no more. */
export const MEMO_PREVIEW_MAX = 300;
/** `MatrixSummary.lastNote.excerpt`: the start of the latest open note. */
export const NOTE_EXCERPT_MAX = 160;

// ── Error codes ──────────────────────────────────────────────────────────────

/** 409 on `PUT /matrices/:id/memo`: the memo changed since `baseUpdatedAt`. */
export const MEMO_CONFLICT = 'MEMO_CONFLICT';
/** 403: a client role writing or switching a note to the `pms` level. */
export const NOTE_VISIBILITY_FORBIDDEN = 'NOTE_VISIBILITY_FORBIDDEN';
/** 403: editing or deleting a note that someone else wrote. */
export const NOTE_AUTHOR_ONLY = 'NOTE_AUTHOR_ONLY';
/** 422: replying to a reply (discussions have a single level). */
export const NOTE_REPLY_TO_REPLY = 'NOTE_REPLY_TO_REPLY';
/** 422: a root-only operation (visibility, resolve, reopen) sent to a reply. */
export const NOTE_ROOT_ONLY = 'NOTE_ROOT_ONLY';

// ── Visibility ───────────────────────────────────────────────────────────────

export const NOTE_VISIBILITIES = ['client', 'pms', 'private'] as const;
/**
 * `client` (default): the PMS team and the users of the company. `pms`: the
 * `pms_user` and `pms_admin` roles. `private`: its author alone.
 */
export const noteVisibilitySchema = z.enum(NOTE_VISIBILITIES);
export type NoteVisibility = z.infer<typeof noteVisibilitySchema>;

export const DEFAULT_NOTE_VISIBILITY: NoteVisibility = 'client';

/** The two global roles that belong to the PMS team. */
export function isPmsRole(role: UserRole): boolean {
  return role === 'pms_user' || role === 'pms_admin';
}

/**
 * Who may pick a level when writing: everyone may write `client` and `private`,
 * only the PMS roles may write `pms`. Shared so that the server enforces what
 * the interface offers.
 */
export function canUseNoteVisibility(role: UserRole, visibility: NoteVisibility): boolean {
  return visibility !== 'pms' || isPmsRole(role);
}

// ── Targets ──────────────────────────────────────────────────────────────────

export const NOTE_TARGET_TYPES = ['matrix', 'cell', 'row', 'column', 'folder'] as const;
export const noteTargetTypeSchema = z.enum(NOTE_TARGET_TYPES);
export type NoteTargetType = z.infer<typeof noteTargetTypeSchema>;

/**
 * An activation row is identified by its order (« 1 » to « 6 »), a dimension
 * row by its id: the same `rowRef` as a cell.
 */
const rowRefSchema = z.string().min(1).max(64);

const cellTargetFields = {
  rowType: cellRowTypeSchema,
  rowRef: rowRefSchema,
  columnNumber: palierNumberSchema,
};
const rowTargetFields = { rowType: cellRowTypeSchema, rowRef: rowRefSchema };
const columnTargetFields = { columnNumber: palierNumberSchema };

const ACTIVATION_ROW_REF = /^[1-6]$/;
const UUID = z.string().uuid();

/** Rejects a `rowRef` that does not fit its `rowType`. */
function rowRefMatchesRowType(target: {
  type: string;
  rowType?: 'activation' | 'dimension';
  rowRef?: string;
}): boolean {
  if (target.type !== 'cell' && target.type !== 'row') return true;
  return target.rowType === 'activation'
    ? ACTIVATION_ROW_REF.test(target.rowRef ?? '')
    : UUID.safeParse(target.rowRef).success;
}

/**
 * Where a note written on a matrix lands: the matrix itself, a cell, a row or a
 * column. No note on a multiple selection or on the activation block, and a
 * folder note has no target to send (the route is the folder route).
 */
export const noteTargetInputSchema = z
  .discriminatedUnion('type', [
    z.object({ type: z.literal('matrix') }),
    z.object({ type: z.literal('cell'), ...cellTargetFields }),
    z.object({ type: z.literal('row'), ...rowTargetFields }),
    z.object({ type: z.literal('column'), ...columnTargetFields }),
  ])
  .refine(rowRefMatchesRowType, {
    message: 'rowRef must be an activation order (1-6) or a dimension id, matching rowType.',
    path: ['rowRef'],
  });
export type NoteTargetInput = z.infer<typeof noteTargetInputSchema>;

/** The target as the server answers: the input shapes plus the folder. */
export const noteTargetSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('matrix') }),
  z.object({ type: z.literal('cell'), ...cellTargetFields }),
  z.object({ type: z.literal('row'), ...rowTargetFields }),
  z.object({ type: z.literal('column'), ...columnTargetFields }),
  z.object({ type: z.literal('folder') }),
]);
export type NoteTarget = z.infer<typeof noteTargetSchema>;

// ── Responses ────────────────────────────────────────────────────────────────

/** A user as notes show them: short display name (« Alice P. »). */
export const noteAuthorSchema = z.object({
  id: z.string().uuid(),
  displayName: z.string(),
});
export type NoteAuthor = z.infer<typeof noteAuthorSchema>;

/** A root note or a reply. `author` is null once the account of the author is gone. */
export const noteSchema = z.object({
  id: z.string().uuid(),
  author: noteAuthorSchema.nullable(),
  visibility: noteVisibilitySchema,
  body: z.string(),
  createdAt: z.string().datetime(),
  /** Set once the note has been edited (« modifiée »). */
  editedAt: z.string().datetime().nullable(),
});
export type Note = z.infer<typeof noteSchema>;

/**
 * A discussion: a root note, its target and its replies (oldest first).
 *
 * `targetLabel` is the label the interface shows for the target (« Matrice »,
 * « <ligne> × <palier> », « Ligne <libellé> », « Palier <libellé> », « Dossier
 * <nom> »). `orphanLabel` is set when the note was written on a dimension that
 * has since been deleted from the draft: the note then comes back as a matrix
 * note (`target.type === 'matrix'`) worded « Portait sur la dimension « X »,
 * supprimée ». `resolvedAt` / `resolvedBy` are null while the discussion is open.
 */
export const noteThreadSchema = noteSchema.extend({
  target: noteTargetSchema,
  targetLabel: z.string(),
  orphanLabel: z.string().nullable(),
  resolvedAt: z.string().datetime().nullable(),
  resolvedBy: noteAuthorSchema.nullable(),
  lastActivityAt: z.string().datetime(),
  replies: z.array(noteSchema),
});
export type NoteThread = z.infer<typeof noteThreadSchema>;

/** The orphan wording: « Portait sur la dimension « X », supprimée ». */
export function formatOrphanLabel(dimensionLabel: string): string {
  return `Portait sur la dimension « ${dimensionLabel} », supprimée`;
}

/** The matrix memo, `text` empty and the two others null while nobody wrote one. */
export const matrixMemoSchema = z.object({
  text: z.string().max(MEMO_MAX),
  updatedBy: noteAuthorSchema.nullable(),
  updatedAt: z.string().datetime().nullable(),
});
export type MatrixMemo = z.infer<typeof matrixMemoSchema>;

/** `GET /matrices/:id/notes`: the memo and every discussion the caller may see. */
export const matrixNotesResponseSchema = z.object({
  memo: matrixMemoSchema,
  threads: z.array(noteThreadSchema),
});
export type MatrixNotesResponse = z.infer<typeof matrixNotesResponseSchema>;

/** `GET /matrix-folders/:id/notes`: the discussions of a folder the caller may see. */
export const folderNotesResponseSchema = z.object({
  threads: z.array(noteThreadSchema),
});
export type FolderNotesResponse = z.infer<typeof folderNotesResponseSchema>;

/** The `details` of a 409 `MEMO_CONFLICT`: who wrote the version the caller did not see. */
export const memoConflictDetailsSchema = z.object({
  updatedBy: noteAuthorSchema.nullable(),
  updatedAt: z.string().datetime().nullable(),
});
export type MemoConflictDetails = z.infer<typeof memoConflictDetailsSchema>;

// ── Inputs ───────────────────────────────────────────────────────────────────

const noteBodySchema = z.string().trim().min(1).max(NOTE_BODY_MAX);

/** `POST /matrices/:id/notes`: a new discussion; the level defaults to `client`. */
export const createMatrixNoteInputSchema = z.object({
  target: noteTargetInputSchema,
  body: noteBodySchema,
  visibility: noteVisibilitySchema.default(DEFAULT_NOTE_VISIBILITY),
});
export type CreateMatrixNoteInput = z.input<typeof createMatrixNoteInputSchema>;

/** `POST /matrix-folders/:id/notes`: a new discussion on a folder. */
export const createFolderNoteInputSchema = z.object({
  body: noteBodySchema,
  visibility: noteVisibilitySchema.default(DEFAULT_NOTE_VISIBILITY),
});
export type CreateFolderNoteInput = z.input<typeof createFolderNoteInputSchema>;

/** `POST /notes/:id/replies`: a reply inherits the level of its root. */
export const replyNoteInputSchema = z.object({ body: noteBodySchema });
export type ReplyNoteInput = z.infer<typeof replyNoteInputSchema>;

/**
 * `PATCH /notes/:id`: the author edits the text, and the level of a root note.
 * An empty patch is a client bug, not a no-op.
 */
export const updateNoteInputSchema = z
  .object({
    body: noteBodySchema.optional(),
    visibility: noteVisibilitySchema.optional(),
  })
  .refine((patch) => patch.body !== undefined || patch.visibility !== undefined, {
    message: 'At least one of body or visibility must be present.',
  });
export type UpdateNoteInput = z.infer<typeof updateNoteInputSchema>;

/**
 * `PUT /matrices/:id/memo`: `baseUpdatedAt` is the `memo.updatedAt` the caller
 * read (null for a memo never written); another value on the server is a 409.
 */
export const updateMemoInputSchema = z.object({
  text: z.string().trim().max(MEMO_MAX),
  baseUpdatedAt: z.string().datetime().nullable(),
});
export type UpdateMemoInput = z.infer<typeof updateMemoInputSchema>;
